// IndexedDB cache of the sync state: snapshot lines as received, a log of later changes, and the outbox of unsent transactions.

import type { BootstrapHeader, Change } from '@confdeck/core-shared';
import type { PendingTx } from './store.ts';

const DB = 'confdeck-sync';
const VERSION = 1;

function req<T>(r: IDBRequest<T>): Promise<T> {
  return new Promise((res, rej) => { r.onsuccess = () => res(r.result); r.onerror = () => rej(r.error); });
}
function done(tx: IDBTransaction): Promise<void> {
  return new Promise((res, rej) => { tx.oncomplete = () => res(); tx.onerror = () => rej(tx.error); tx.onabort = () => rej(tx.error); });
}

/** `lines` reads the snapshot in small batches, so the first page renders before the rest is read. */
export interface CachedState { header: BootstrapHeader; syncId: number; lines: AsyncIterable<string>; changes: Change[]; outbox: PendingTx[] }

/**
 * One cache per workspace host. Stores: `kv` (header, syncId), `lines` (snapshot lines in order),
 * `changes` (applied after the snapshot), `outbox` (transactions the server has not confirmed).
 */
export class SyncCache {
  /** This tab's id (one cache per tab): its own unsent edits in the shared outbox are told from another tab's. */
  private readonly tab = typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : String(Math.random());
  private db: Promise<IDBDatabase> | null = null;
  readonly scope: string;
  constructor(scope: string) { this.scope = scope; }

  private open(): Promise<IDBDatabase> {
    if (!this.db) {
      this.db = new Promise((res, rej) => {
        const r = indexedDB.open(`${DB}:${this.scope}`, VERSION);
        r.onupgradeneeded = () => {
          const d = r.result;
          for (const s of ['kv', 'lines', 'changes', 'outbox']) if (!d.objectStoreNames.contains(s)) d.createObjectStore(s);
        };
        r.onsuccess = () => res(r.result);
        r.onerror = () => rej(r.error);
      });
    }
    return this.db;
  }

  async load(): Promise<CachedState | null> {
    try {
      const d = await this.open();
      const tx = d.transaction(['kv', 'lines', 'changes', 'outbox'], 'readonly');
      const [header, syncId, count, changes, outbox] = await Promise.all([
        req(tx.objectStore('kv').get('header')) as Promise<BootstrapHeader | undefined>,
        req(tx.objectStore('kv').get('syncId')) as Promise<number | undefined>,
        req(tx.objectStore('lines').count()),
        req(tx.objectStore('changes').getAll()) as Promise<Change[][]>,
        req(tx.objectStore('outbox').getAll()) as Promise<PendingTx[]>,
      ]);
      if (!header || !count) return null;
      async function* lines() {
        // Two lines first (schema and the first page of the opened section), then bigger batches.
        let from = 0, step = 2;
        while (from < count) {
          const batch = await req(d.transaction('lines').objectStore('lines').getAll(IDBKeyRange.bound(from, from + step - 1))) as string[];
          for (const l of batch) yield l;
          from += step;
          step = 8;
        }
      }
      return { header, syncId: syncId ?? header.syncId, lines: lines(), changes: changes.flat(), outbox };
    } catch {
      // A browser without storage (private mode, blocked site data) simply starts from the network.
      return null;
    }
  }

  /**
   * Replaces the cached snapshot. Changes logged after the snapshot's syncId stay (audit 28.10, P1-5): they arrived over the socket
   * while the snapshot downloaded, and dropping them would leave a gap the next start never fetches again.
   */
  async saveSnapshot(header: BootstrapHeader, lines: string[]): Promise<void> {
    try {
      const d = await this.open();
      const tx = d.transaction(['kv', 'lines', 'changes'], 'readwrite');
      const store = tx.objectStore('changes');
      const [keys, batches, last] = await Promise.all([req(store.getAllKeys()), req(store.getAll()) as Promise<Change[][]>, req(tx.objectStore('kv').get('syncId')) as Promise<number | undefined>]);
      tx.objectStore('lines').clear();
      keys.forEach((k, i) => {
        const later = (batches[i] ?? []).filter((c) => c.syncId > header.syncId);
        if (later.length) store.put(later, k); else store.delete(k);
      });
      lines.forEach((l, i) => tx.objectStore('lines').put(l, i));
      tx.objectStore('kv').put(header, 'header');
      tx.objectStore('kv').put(Math.max(header.syncId, Number(last ?? 0)), 'syncId');
      await done(tx);
    } catch { /* cache is an optimisation; the app keeps working without it */ }
  }

  private seq = Date.now();
  async appendChanges(changes: Change[], syncId: number): Promise<void> {
    if (!changes.length) return;
    try {
      const d = await this.open();
      const tx = d.transaction(['kv', 'changes'], 'readwrite');
      tx.objectStore('changes').put(changes, this.seq++);
      tx.objectStore('kv').put(syncId, 'syncId');
      await done(tx);
    } catch { /* see saveSnapshot */ }
  }

  /**
   * The outbox is the only copy of edits the server has not confirmed, so a failed write is not swallowed: the caller shows it.
   * It is stored with its user, so another person logging in on this browser never sends someone else's edits.
   */
  async saveOutbox(pending: PendingTx[], userId: string | null = null): Promise<void> {
    const d = await this.open();
    const tx = d.transaction(['kv', 'outbox'], 'readwrite');
    const s = tx.objectStore('outbox');
    // The base is shared by the tabs of one host: a tab replaces only its own edits, another tab's unsent ones stay
    // (audit 28.10, P1-6). A repeated edit is answered by the server as a repeat, so a tab that sends them too is harmless.
    const [keys, all, owner] = await Promise.all([req(s.getAllKeys()), req(s.getAll()) as Promise<(PendingTx & { tab?: string })[]>, req(tx.objectStore('kv').get('outboxUser'))]);
    if (owner !== userId) s.clear();
    else keys.forEach((k, i) => { if (all[i]?.tab === this.tab || all[i]?.tab === undefined) s.delete(k); });
    pending.forEach((p) => s.put({ ...p, tab: this.tab }, p.clientTxId));
    tx.objectStore('kv').put(userId, 'outboxUser');
    await done(tx);
  }

  /** Unsent edits of this user, kept across a resync. */
  async loadOutbox(userId: string): Promise<PendingTx[]> {
    try {
      const d = await this.open();
      const tx = d.transaction(['kv', 'outbox'], 'readonly');
      const [owner, outbox] = await Promise.all([req(tx.objectStore('kv').get('outboxUser')), req(tx.objectStore('outbox').getAll()) as Promise<PendingTx[]>]);
      return owner === userId ? outbox : [];
    } catch { return []; }
  }

  async changeCount(): Promise<number> {
    try {
      const d = await this.open();
      return await req(d.transaction('changes').objectStore('changes').count());
    } catch { return 0; }
  }

  /** Drops the cached data; `keepOutbox` leaves unsent edits for the reload after a resync. */
  async clear(keepOutbox = false): Promise<void> {
    try {
      const d = await this.open();
      const tx = d.transaction(['kv', 'lines', 'changes', 'outbox'], 'readwrite');
      tx.objectStore('lines').clear();
      tx.objectStore('changes').clear();
      if (keepOutbox) { tx.objectStore('kv').delete('header'); tx.objectStore('kv').delete('syncId'); }
      else { tx.objectStore('kv').clear(); tx.objectStore('outbox').clear(); }
      await done(tx);
    } catch { /* see saveSnapshot */ }
  }
}
